import { OTLayout } from "@/components/layout/ot-layout";
import { useState, useCallback, useEffect } from "react";
import { Brain, Plus, Trash2, ChevronDown, ChevronUp, Loader2, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";

type Framework = "kolb" | "johns";

const KOLB_STAGES = [
  { key: "concrete_experience", label: "Concrete Experience", prompt: "Describe the specific situation, event, or experience you are reflecting on. What happened? What did you do? Who else was involved?" },
  { key: "reflective_observation", label: "Reflective Observation", prompt: "What did you notice during and after the experience? What were your thoughts and feelings? What surprised you?" },
  { key: "abstract_conceptualisation", label: "Abstract Conceptualisation", prompt: "What sense can you make of this experience? What theory, evidence, or models help you understand what happened? What principles apply?" },
  { key: "active_experimentation", label: "Active Experimentation", prompt: "What will you do differently as a result of this reflection? What changes will you make to your practice? How will you test new approaches?" },
];

const JOHNS_STAGES = [
  { key: "description", label: "Description", prompt: "Describe the situation: What happened? What was the context? What were the key events? What were your aims?" },
  { key: "feelings", label: "Feelings & Thoughts", prompt: "What were you thinking and feeling at the time? What factors influenced how you were thinking and feeling?" },
  { key: "evaluation", label: "Evaluation", prompt: "What was good or bad about the experience? What went well and what went less well? What were the consequences of your actions?" },
  { key: "analysis", label: "Analysis", prompt: "Why did things happen as they did? What knowledge or evidence informs this situation? How does the experience connect to RCOT Code of Ethics or HCPC Standards?" },
  { key: "conclusion", label: "Conclusion", prompt: "What else could you have done? What would have been the best way to handle this situation? What have you learned?" },
  { key: "action_plan", label: "Action Plan", prompt: "If a similar situation arose again, what would you do? What specific steps will you take to improve your practice as a result of this reflection?" },
];

const FRAMEWORK_LABELS: Record<Framework, string> = {
  kolb: "Kolb's Experiential Learning Cycle",
  johns: "Johns' Model of Structured Reflection",
};

interface ReflectionEntry {
  id: number;
  framework: Framework;
  title: string;
  linkedCpdTitle: string | null;
  stages: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

interface AddFormProps {
  onSaved: (entry: ReflectionEntry) => void;
  onCancel: () => void;
}

function AddReflectionForm({ onSaved, onCancel }: AddFormProps) {
  const [framework, setFramework] = useState<Framework>("kolb");
  const [title, setTitle] = useState("");
  const [stages, setStages] = useState<Record<string, string>>({});
  const [linkedCpd, setLinkedCpd] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stageList = framework === "kolb" ? KOLB_STAGES : JOHNS_STAGES;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/ot/reflections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          framework,
          title: title.trim(),
          linkedCpdTitle: linkedCpd.trim() || undefined,
          stages,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      const created: ReflectionEntry = await res.json();
      onSaved(created);
    } catch {
      setError("Failed to save reflection. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-amber-200 p-6 space-y-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">New Reflection Entry</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
      </div>

      <div>
        <p className="block text-xs font-medium text-slate-600 mb-1.5">Reflective Framework *</p>
        <div className="flex gap-3 flex-wrap">
          {(["kolb", "johns"] as Framework[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => { setFramework(f); setStages({}); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                framework === f
                  ? "bg-amber-700 text-white border-amber-700"
                  : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
            >
              {f === "kolb" ? "Kolb's Cycle" : "Johns' Model"}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-1.5">{FRAMEWORK_LABELS[framework]}</p>
      </div>

      <div>
        <label htmlFor="ot-ref-title" className="block text-xs font-medium text-slate-600 mb-1.5">Reflection Title *</label>
        <input
          id="ot-ref-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Managing a complex fatigue presentation with comorbid depression"
          required
          className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>

      <div>
        <label htmlFor="ot-ref-linked" className="block text-xs font-medium text-slate-600 mb-1.5">Link to CPD Activity (optional)</label>
        <input
          id="ot-ref-linked"
          value={linkedCpd}
          onChange={(e) => setLinkedCpd(e.target.value)}
          placeholder="e.g. RCOT Annual Conference — Fatigue Management Workshop"
          className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>

      <div className="space-y-4">
        {stageList.map((stage) => (
          <div key={stage.key} className="border border-slate-200 rounded-xl p-4 space-y-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">{stage.label}</p>
              <p className="text-xs text-slate-500 leading-relaxed mt-0.5">{stage.prompt}</p>
            </div>
            <div className="flex justify-end">
              <VoiceInputButton
                tone="amber"
                size="xs"
                onTranscript={(chunk) => setStages(prev => ({ ...prev, [stage.key]: appendVoiceChunk(prev[stage.key] ?? "", chunk) }))}
                label={`Dictate ${stage.label}`}
              />
            </div>
            <textarea
              value={stages[stage.key] ?? ""}
              onChange={(e) => setStages(prev => ({ ...prev, [stage.key]: e.target.value }))}
              rows={3}
              placeholder="Your reflection..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
          </div>
        ))}
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

      <div className="flex gap-3">
        <Button type="submit" disabled={saving} className="bg-amber-700 hover:bg-amber-800 text-white gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {saving ? "Saving..." : "Save Reflection"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
}

export default function OtReflectionTool() {
  const [reflections, setReflections] = useState<ReflectionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/ot/reflections");
      if (res.ok) setReflections(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (entry: ReflectionEntry) => {
    setReflections(prev => [entry, ...prev]);
    setShowForm(false);
    setExpandedId(entry.id);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this reflection entry? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/ot/reflections/${id}`, { method: "DELETE" });
      setReflections(prev => prev.filter(r => r.id !== id));
      if (expandedId === id) setExpandedId(null);
    } finally {
      setDeletingId(null);
    }
  };

  const getStageList = (fw: Framework) => fw === "kolb" ? KOLB_STAGES : JOHNS_STAGES;

  return (
    <OTLayout title="Structured Reflection Tool">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-700 to-rose-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">Structured Reflection Tool</h2>
              <p className="text-amber-200 text-sm">Kolb's Experiential Cycle · Johns' Model of Structured Reflection</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            Use guided reflective frameworks to deepen learning from clinical experiences and CPD activities. Reflections are saved to your secure account and can be linked to CPD activities or your RCOT Advanced Practice Portfolio.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
            <h3 className="font-bold text-amber-800 mb-2">Kolb's Experiential Learning Cycle</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">A four-stage model: Concrete Experience → Reflective Observation → Abstract Conceptualisation → Active Experimentation. Ideal for reflecting on clinical encounters and practice-based learning.</p>
            <ol className="text-xs text-slate-500 space-y-1">
              {KOLB_STAGES.map((s, i) => <li key={s.key}>{i + 1}. {s.label}</li>)}
            </ol>
          </div>
          <div className="bg-orange-50 rounded-2xl border border-orange-200 p-5">
            <h3 className="font-bold text-orange-800 mb-2">Johns' Model of Structured Reflection</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">A six-stage guided reflection model particularly useful for complex clinical situations and professional development. RCOT-endorsed for OT CPD reflective writing.</p>
            <ol className="text-xs text-slate-500 space-y-1">
              {JOHNS_STAGES.map((s, i) => <li key={s.key}>{i + 1}. {s.label}</li>)}
            </ol>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">
            My Reflection Entries
            {!loading && <span className="ml-2 text-slate-400 font-normal text-sm">({reflections.length})</span>}
          </h3>
          <Button onClick={() => setShowForm(true)} className="bg-amber-700 hover:bg-amber-800 text-white gap-2">
            <Plus className="h-4 w-4" /> New Reflection
          </Button>
        </div>

        {showForm && (
          <AddReflectionForm onSaved={handleSaved} onCancel={() => setShowForm(false)} />
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-2xl" />)}
          </div>
        ) : reflections.length === 0 && !showForm ? (
          <div className="text-center py-16 text-slate-500">
            <Brain className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No reflection entries yet</p>
            <p className="text-sm mt-1">Use the reflection tool to record and structure your professional learning.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reflections.map((r) => {
              const stageList = getStageList(r.framework);
              const expanded = expandedId === r.id;
              return (
                <div key={r.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedId(expanded ? null : r.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedId(expanded ? null : r.id); }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                          {r.framework === "kolb" ? "Kolb" : "Johns"}
                        </span>
                        {r.linkedCpdTitle && (
                          <span className="text-xs text-slate-400 truncate">Linked: {r.linkedCpdTitle}</span>
                        )}
                      </div>
                      <p className="font-semibold text-slate-900 text-sm">{r.title}</p>
                      <p className="text-xs text-slate-500">{new Date(r.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                  </div>
                  {expanded && (
                    <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-3">
                      {stageList.map((stage) => {
                        const val = r.stages[stage.key];
                        if (!val) return null;
                        return (
                          <div key={stage.key}>
                            <p className="text-xs font-semibold text-slate-500 mb-1">{stage.label}</p>
                            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{val}</p>
                          </div>
                        );
                      })}
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleDelete(r.id)}
                          disabled={deletingId === r.id}
                          className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          {deletingId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Data storage:</strong> Reflection entries are saved to your secure RheumHub account and persist across devices and sessions. They support your HCPC CPD audit submission and can be linked to your Advanced Practice Portfolio. Entries are only visible to you.
        </div>
      </div>
    </OTLayout>
  );
}
